import React from 'react';
import { Link } from 'react-router-dom';

const AMinuteWithGod = () => {
  const languages = [
    { 
      id: 'kissi', 
      name: 'Kissi', 
      description: 'For our Kissi-speaking listeners in Sierra Leone, Liberia, and Guinea',
      path: '/a-minute-with-god/kissi',
      region: 'Sierra Leone • Liberia • Guinea'
    },
    { 
      id: 'english', 
      name: 'English', 
      description: 'For our English-speaking audience across West Africa',
      path: '/a-minute-with-god/english',
      region: 'West Africa'
    },
    { 
      id: 'french', 
      name: 'French', 
      description: 'Pour nos auditeurs francophones en Guinée',
      path: '/a-minute-with-god/french',
      region: 'Guinea • Francophone Africa'
    },
    { 
      id: 'mandingo', 
      name: 'Mandingo', 
      description: 'For Mandingo/Mandinka speakers across the Mano River region',
      path: '/a-minute-with-god/mandingo',
      region: 'Mano River Region'
    },
    { 
      id: 'fulani', 
      name: 'Fulani', 
      description: 'For our Fulani/Fula/Pulaar listeners',
      path: '/a-minute-with-god/fulani',
      region: 'Guinea • West Africa'
    },
    { 
      id: 'gbandi', 
      name: 'Gbandi', 
      description: 'For Gbandi speakers in Lofa County, Liberia',
      path: '/a-minute-with-god/gbandi',
      region: 'Lofa County, Liberia'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Matching Kioo Radio Style */}
      <section className="relative bg-gradient-to-br from-kioo-primary via-kioo-secondary to-kioo-dark text-white py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        
        {/* Background Pattern */}
        <div className="absolute top-10 right-10 opacity-10">
          <div className="w-32 h-32 border-4 border-white rounded-full"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <span className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 rounded-full text-sm font-medium backdrop-blur-sm">
              ✝️ Audio Bible Resources
            </span>
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            A Minute with God
          </h1>
          <p className="text-xl text-green-100 max-w-2xl mx-auto mb-8">
            Audio Bible devotions in your heart language. Listen, be blessed, and download to share with others.
          </p>
          
          <div className="flex justify-center gap-4">
            <a 
              href="https://globalrecordings.net" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-6 py-3 bg-white text-kioo-primary rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Visit Global Recordings
            </a>
            <a 
              href="https://www.bible.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-6 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-kioo-primary transition-colors"
            >
              Bible.com
            </a>
          </div>
        </div>
      </section>

      {/* Language Selection */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Select Your Language</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Choose your preferred language to access free audio Bible resources from Global Recordings Network and Bible.com
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {languages.map(lang => (
              <Link
                key={lang.id}
                to={lang.path}
                className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-kioo-primary to-kioo-secondary p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-white">{lang.name}</h3>
                    <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 0112.728 0" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Card Body */}
                <div className="p-5">
                  <p className="text-gray-600 text-sm mb-3">{lang.description}</p>
                  <div className="flex items-center text-xs text-gray-500 mb-4">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {lang.region}
                  </div>
                  <div className="flex items-center text-kioo-primary font-medium group-hover:translate-x-2 transition-transform">
                    <span>Access Audio Bible</span>
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-kioo-primary to-kioo-secondary rounded-2xl overflow-hidden shadow-lg">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 lg:p-12 text-white">
                <h2 className="text-2xl font-bold mb-4">📚 More Audio Bible Resources</h2>
                <p className="text-green-100 mb-6">
                  Access thousands of free audio Bible recordings in over 6,500 languages from these trusted ministry partners.
                </p>
                <div className="space-y-3">
                  <a 
                    href="https://globalrecordings.net" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-white hover:text-green-200 transition-colors"
                  >
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    Global Recordings Network - 6,500+ languages
                  </a>
                  <a 
                    href="https://www.faithcomesbyhearing.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-white hover:text-green-200 transition-colors"
                  >
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    Faith Comes By Hearing - 1,700+ languages
                  </a>
                  <a 
                    href="https://www.bible.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-white hover:text-green-200 transition-colors"
                  >
                    <span className="w-2 h-2 bg-white rounded-full mr-3"></span>
                    YouVersion Bible App - 2,000+ versions
                  </a>
                </div>
              </div>
              <div className="bg-kioo-dark bg-opacity-30 p-8 lg:p-12 flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="text-6xl mb-4">🎧</div>
                  <p className="text-lg font-medium">Free for personal & ministry use</p>
                  <p className="text-green-200 text-sm mt-2">Download and share with your community</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>Audio resources provided by Global Recordings Network, Faith Comes By Hearing, and YouVersion.</p>
          <p className="mt-1">All audio files are free for personal and ministry use.</p>
        </div>
      </section>
    </div>
  );
};

export default AMinuteWithGod;
